import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  FieldType,
  PaymentMethod,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import { SpecialQuestionKeys } from '@platform-ui/utilities/application/configs/standards/types.js';
import { eq } from '../../ast/utils.js';
import STANDARD_COMPONENTS from '../standards/index.js';

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'welcome-and-overview',
      layout: 'welcome',
      title: 'WELCOME AND OVERVIEW',
      body: `Welcome to the Fairfax County Economic Mobility Pilot!   
 
The purpose of this participant consent form is to share important information and considerations about participating in the pilot and receiving cash payments over a 15-month period.  
       
It is important to understand all aspects of this program before deciding whether you want to participate. If you decide to participate, you will sign the form electronically at the end.  
      `,
    },
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: 'Participant Consent Form',
        message:
          'The purpose of this participant consent form is to share important information and considerations about participating in the pilot and receiving cash payments over a 15-month period.',
      },
      {
        status: 'AwaitingReview',
        message: 'Thank you for submitting your consent form! Check back here for updates.',
      },
      {
        status: 'InReview',
        message: 'Thank you for submitting your consent form! Check back here for updates.',
      },
      {
        status: 'InProgress',
        message: 'You’ve made great progress so far! Keep working on your consent form.',
      },
    ],
    submission: {
      title: 'What happens next?',
      bullets: [
        {
          key: 'payments',
          title: 'Receiving a payment',
          icon: 'money',
          description: 'Participants will receive the first payment by the end of October 2023.',
        },
      ],
    },
  },
  sections: [
    {
      key: 'about-the-program',
      name: 'ABOUT THE PROGRAM',
      overview: {
        title: 'ABOUT THE PROGRAM',
        description: 'Information about the program',
      },
      questionGroups: [
        {
          key: 'aboutTheProgram',
          name: 'ABOUT THE PROGRAM',
          questions: [
            {
              key: 'aboutTheProgram',
              copy: { title: 'ABOUT THE PROGRAM' },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Typography,
                  key: 'aboutTheProgram.description',
                  copy: `**Program Title:** Fairfax County Economic Mobility Pilot (“FCEMP”)

**Program Duration:** 15 months 

**Fairfax County Economic Mobility Pilot:** The pilot will provide a monthly cash payment to randomly selected Fairfax County residents based on the eligibility criteria with the goal of supporting the economic well-being of families and economic mobility/stability. The pilot is led by Fairfax County, and the United Way of the National Capital Area and their Financial Empowerment Center, in partnership with Britepaths.
              `,
                  props: { variant: 'largeBody', alignment: 'left' },
                },
                {
                  type: FieldType.Checkbox,
                  key: 'aboutTheProgram.confirmation',
                  copy: 'I have read the above information',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: 'pilot-services',
      name: 'PILOT SERVICES',
      overview: {
        title: 'PILOT SERVICES',
        description: 'Information about the pilot services',
      },
      questionGroups: [
        {
          key: 'pilotServices',
          name: 'PILOT SERVICES',
          questions: [
            {
              key: 'pilotServices',
              copy: { title: 'PILOT SERVICES' },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Typography,
                  key: 'pilotServices.description',
                  copy: `**Pilot Services:** The pilot will provide the following services: 
* Cash Assistance Payments ($750 per month for 15 months) 
* Optional Economic Mobility Coaching Services for 21 months by the United Way NCA Financial Empowerment Center at South County`,
                  props: { variant: 'largeBody', alignment: 'left' },
                },
                {
                  key: 'consentForWellnessSupports',
                  options: YesNoOptions,
                  type: FieldType.RadioList,
                  copy: 'I would like to learn more about optional financial wellness supports available in addition to participating in the FCEMP. Please share my name and contact information with United Way NCA Financial Empowerment Center at South County.',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: 'other-pilot-terms',
      name: 'OTHER PILOT TERMS',
      overview: {
        title: 'OTHER PILOT TERMS',
        description: 'Information about other pilot terms',
      },
      questionGroups: [
        {
          key: 'otherPilotTerms',
          name: 'OTHER PILOT TERMS',
          questions: [
            {
              key: 'otherPilotTerms',
              copy: { title: 'OTHER PILOT TERMS' },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Typography,
                  key: 'otherPilotTerms.description',
                  copy: `**Possible Impact to Public Assistance Benefits**  
Payments from the pilot may affect your eligibility for public assistance benefits.  

**Taxes**  
Payments from the pilot may affect income taxes. Participants should review relevant information from the Internal Revenue Service. 

**Termination**         
This pilot is designed to support the well-being and economic mobility of families that reside in Fairfax County. As such, your enrollment in this program may be terminated in certain circumstances. Specifically, your participation may be terminated for the following reasons: 
* If you voluntarily withdraw from the program (see below); 
* If you knowingly misrepresent your identity or eligibility to project staff; 
* If you move outside of Fairfax County or the cities of Fairfax or Falls Church; 
* If you are deceased 

Termination from the pilot means you will no longer have access to the monthly cash payments or optional economic mobility coaching. You will be notified in writing by the FCEMP if your participation is terminated.

**Voluntary Withdrawal**  
You may choose to withdraw from the pilot at any time by notifying the FCEMP in writing. You can inform us by email at [GIpilot@fairfaxcounty.gov](mailto:GIpilot@fairfaxcounty.gov).  Once you withdraw from the pilot, you cannot be reinstated into the program. Before we end your payments, we will send an email to confirm your withdrawal. 
                  `,
                  props: { variant: 'largeBody', alignment: 'left' },
                },
                {
                  type: FieldType.Checkbox,
                  key: 'otherPilotTerms.confirmation',
                  copy: 'I have read the above information',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: 'research',
      name: 'RESEARCH',
      overview: {
        title: 'RESEARCH',
        description: 'Information about research',
      },
      questionGroups: [
        {
          key: 'research',
          name: 'RESEARCH',
          questions: [
            {
              key: 'research',
              copy: { title: 'RESEARCH' },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Typography,
                  key: 'research.description',
                  copy: `To learn about how the Economic Mobility Pilot program is working, we have joined with a team of researchers at George Mason University to better understand how the program is affecting you and your families.  

When you completed your application, you shared your language preference, household size and income, employment and education, race and ethnicity, gender identification and disability status. In order to understand who the pilot program is serving, that information will be shared with the George Mason research team. 

* Your name and address will not be shared with this information.
* None of the documentation you uploaded (identity documents, proof of income or school enrollment) will be shared. 

Separate from this information, your email and telephone number will be shared with George Mason researchers so they may contact you about **voluntarily** participating in surveys and interviews about the Economic Mobility Pilot. Participation in this research will not impact your enrollment in the program. All participants who complete the interviews or surveys will earn additional income for their families.
`,
                  props: { variant: 'largeBody', alignment: 'left' },
                },
                {
                  type: FieldType.Checkbox,
                  key: 'research.confirmation',
                  copy: 'I have read the above information',
                },
              ],
            },
            {
              key: 'researchParticipation',
              copy: { title: 'RESEARCH PARTICIPATION' },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Typography,
                  key: 'research.participation',
                  copy: `To learn about how the Economic Mobility Pilot is working, we have joined with a team of researchers at George Mason University. We want to better understand how the program is affecting you and your families.  

The researchers at George Mason are asking for volunteers for individual and group interviews. What you share in the interviews will be kept confidential.

Your experiences are extremely valuable to helping this program succeed in the long term. 

**Participants who agree to be interviewed will earn additional income for their families.** 

For more information on how to participate in the study, [click here](https://gmuchss.az1.qualtrics.com/jfe/form/SV_8dZtvS8psHWIATk).`,
                  props: { variant: 'largeBody', alignment: 'left' },
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: 'programParticipation',
      name: 'PROGRAM PARTICIPATION',
      overview: {
        title: 'PROGRAM PARTICIPATION',
        description: 'Information about program participation',
      },
      questionGroups: [
        {
          key: 'programParticipation',
          name: 'PROGRAM PARTICIPATION',
          questions: [
            {
              key: 'programParticipation',
              copy: { title: 'PROGRAM PARTICIPATION' },
              layout: 'panel',
              fields: [
                {
                  type: FieldType.Typography,
                  key: 'programParticipation.description',
                  copy: `**Program Participation:** It is important that you read the description of the program and ask questions, so you understand what to expect if you decide to participate. Please consider the information carefully to make sure the program is a good fit and to understand the possible impacts participation may have on you.

**Participant Agreement**  
You agree that you meet and will continue to meet the following eligibility criteria:

* Are 18 years of age or older;
* Are currently employed; 
* Have at least one child aged 16 years old or younger living in the household; at the time of selection.
* Reside in one of the identified neighborhoods in one of the following zip codes: 22306, 22309, 20190, 20191, 20170, 22003, 22150, 20120, 2015;
* Have a total gross household income that falls between 150%-250% of the 2023 Federal Poverty Level.                  
`,
                  props: { variant: 'largeBody', alignment: 'left' },
                },
                {
                  type: FieldType.Checkbox,
                  key: 'programParticipation.confirmation',
                  copy: 'I have read the above information',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: 'preferredPaymentMethod',
      name: 'PAYMENT INFORMATION',
      overview: {
        title: 'PAYMENT INFORMATION',
        description: 'How you would like to receive your monthly payments',
      },
      questionGroups: [
        {
          key: 'preferredPaymentMethod',
          name: 'PAYMENT INFORMATION',
          questions: [
            mergeObjects(
              [
                STANDARD_COMPONENTS.definitions.questions.preferredPaymentMethod,
                {
                  copy: {
                    title: 'How would you like to receive your monthly payments?',
                  },
                  fields: [
                    {
                      type: FieldType.RadioList,
                      key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
                      options: [
                        {
                          label: 'ACH Direct Deposit',
                          description:
                            'You will be provided with a secure link below to enter your bank account and routing information so that we can issue payments directly to your bank account.',
                          value: PaymentMethod.DirectDeposit,
                        },
                        {
                          label: 'Prepaid Debit Card (Physical)',
                          description:
                            'We will mail you a physical debit card. There are no restrictions on how you can use your debit card.',
                          value: PaymentMethod.PhysicalCard,
                        },
                        {
                          label: 'Prepaid Debit Card (Virtual) ',
                          description:
                            'You will be emailed a link to claim your virtual debit card online. There are no restriction on how you can use your virtual debit card; however, you will be unable to use an ATM.',
                          value: PaymentMethod.VirtualCard,
                        },
                      ],
                    },
                  ],
                } as ApplicationQuestion,
              ],
              'merge',
            ),
            {
              key: SpecialQuestionKeys.BankAccount,
              copy: { title: 'BANK ACCOUNT INFORMATION' },
              fields: [],
              dynamicLogic: eq('preferredPaymentMethod', PaymentMethod.DirectDeposit),
            },
          ],
        },
      ],
    },
    mergeObjects(
      [
        STANDARD_COMPONENTS.definitions.sections.review,
        {
          overview: {
            description: `Please make sure all the information you provided is correct. 
You will not have an opportunity to make any changes to your application once it is submitted.`,
          },
          questionGroups: [
            undefined,
            {
              questions: [
                {
                  key: 'consent',
                  copy: {
                    title: 'CONSENT',
                  },
                  layout: 'panel',
                  fields: [
                    {
                      type: FieldType.Typography,
                      key: 'consent.intro',
                      copy: `**Signing the Consent Form:** By signing below, you are agreeing that: 
* You have read this form (or someone has read it to you); 
* You have had the opportunity to ask questions and have had them answered to your satisfaction.
* You have provided accurate and truthful information to the best of your knowledge. 
* You voluntarily agree to participate in the program.`,
                      props: { variant: 'body', alignment: 'left' },
                    },
                    {
                      type: FieldType.Text,
                      key: 'consent.name',
                      inputType: TextInputType.Text,
                      copy: 'Participant Name:',
                    },
                    {
                      type: FieldType.Text,
                      key: 'consent.signature',
                      inputType: TextInputType.Signature,
                      copy: 'Participant Signature:',
                    },
                    {
                      type: FieldType.Date,
                      key: 'consent.date',
                      copy: 'Date:',
                      validation: { required: true, allowedRange: 'today' },
                    },
                  ],
                },
              ],
            },
          ],
        } as ApplicationSection,
      ],
      'merge',
    ),
  ],
};

export default CONFIG;
